import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Search, Gavel, Info, Menu, X, LogIn, Tv, Radio, Film, Smartphone, Radar as RadarIcon, Users, Newspaper, MessagesSquare, Tags, BookOpen } from "lucide-react";
import { getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useSiteUpdates, { markVisited } from "@/lib/useSiteUpdates";
import { isNavBadgeActiveFor } from "@/lib/radarBadge";
import AccessibilityBadge from "@/components/AccessibilityBadge";
import NotificationsBadge from "@/components/NotificationsBadge";
import TextSizeControl from "@/components/TextSizeControl";

// Tiny red dot rendered next to a nav link when the underlying section
// (`videos`, `podcasts`, `artigos`, `cursos`, `blog`) has content newer
// than the visitor's last click on that link. Visually similar to
// notification dots in iOS/macOS — minimal but instantly recognisable.
function NewDot({ pulse = false }) {
  return (
    <span
      data-testid="nav-new-dot"
      aria-label="Conteúdo novo"
      className={`inline-block w-1.5 h-1.5 rounded-full bg-red-500 ml-1 align-middle ${pulse ? "animate-ping absolute" : ""}`}
    />
  );
}

// Pill vermelho "Novo" (animação ping). Usado no link da nav que está
// configurado como ``NAV_BADGE_TARGET`` em /lib/radarBadge.js.
function NewBadgePill({ testid, mobile = false }) {
  if (mobile) {
    return (
      <span
        className="inline-flex px-1.5 py-[1px] rounded-full bg-red-500 text-[9px] uppercase tracking-widest font-bold text-white animate-pulse"
        data-testid={testid}
      >
        Novo
      </span>
    );
  }
  return (
    <span
      aria-label="Novidade"
      data-testid={testid}
      className="relative inline-flex items-center"
    >
      <span className="absolute inset-0 inline-flex rounded-full bg-red-500/60 animate-ping" />
      <span className="relative inline-flex px-1.5 py-[1px] rounded-full bg-red-500 text-[8px] uppercase tracking-widest font-bold text-white border border-red-300/70 shadow">
        Novo
      </span>
    </span>
  );
}

export default function Navigation() {
  const [q, setQ] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = !!getToken();
  const { user } = useAuth();
  const { hasNewSince, anyNew } = useSiteUpdates();
  // Badge "Novo" — controlado por ``NAV_BADGE_TARGET`` em /lib/radarBadge.js.
  // Para mover o badge para outro link da nav, edite aquela constante.
  const radarIsNew = isNavBadgeActiveFor("radar");
  const tvIsNew = isNavBadgeActiveFor("tv");
  const comunidadeIsNew = isNavBadgeActiveFor("comunidade");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/busca?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header
      data-testid="top-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-slate-950/80 border-b border-white/5" : "bg-gradient-to-b from-slate-950/80 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group shrink-0">
          <span className="relative">
            <span className="absolute inset-0 bg-red-600 blur-md opacity-50 group-hover:opacity-80 transition"></span>
            <Gavel className="relative w-6 h-6 text-red-500" />
            {/* Pulsing dot on the brand mark when ANY section has fresh
                content the visitor hasn't seen yet. Pulls the eye to the
                logo on return visits, suggesting "the site is alive". */}
            {anyNew && (
              <span aria-hidden className="absolute -top-0.5 -right-0.5 flex w-2 h-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-red-500 border border-slate-950" />
              </span>
            )}
          </span>
          <span className="font-serif text-2xl font-bold tracking-tight" translate="no">
            Juris<span className="text-red-500">tube</span>
          </span>
        </Link>

        {/* (2026-05-21) Tightened the nav gap from `gap-5 lg:gap-6` to
            `gap-3.5 lg:gap-5` and the search input from `w-24 lg:w-36` to
            `w-16 lg:w-24` so the right-hand "Entrar" + "Sobre o JurisTube"
            buttons no longer get pushed off-screen on 13–14" laptops
            (≥1280 viewports). Every page already has its own page-level
            search, so the header field can be a compact compass rather
            than the main entry point.
            (2026-02-28 / Iter307.2) Reduzido gap horizontal e fonte para
            13px em lg/xl porque a barra passou a ter 11 links + botões
            de acessibilidade, estourando notebooks a 100% de zoom. */}
        <nav className="hidden md:flex items-center gap-2 lg:gap-2.5 text-[13px] text-slate-300">
          <Link to="/" className="hover:text-white transition-colors" data-testid="nav-home">Início</Link>
          {/* Iter250 — "Vídeos" e "Podcasts" fundidos em "Podcasts de Vídeo".
              (2026-02-28) Compactado para "Podcast" + ícone Film para
              não quebrar em duas linhas em notebooks 13–14" e ficar
              alinhado visualmente com TV/Rádio (mesmo stroke da lucide). */}
          <Link
            to="/podcasts-de-video"
            onClick={() => markVisited("podcasts")}
            className="relative inline-flex items-center gap-1 whitespace-nowrap hover:text-white transition-colors"
            data-testid="nav-podcasts"
            title="Podcasts de Vídeo — episódios completos"
          >
            <Film className="w-3.5 h-3.5" /> Podcast{hasNewSince("podcasts") && <NewDot />}
          </Link>
          {/* TV JurisTube — theater mode com TV Justiça (ao vivo) + acervo
              em shuffle. Compactado em "TV" para caber bem na barra. */}
          <Link
            to="/tv"
            className="relative inline-flex items-center gap-1 hover:text-amber-300 transition-colors font-semibold"
            data-testid="nav-tv"
            title="TV JurisTube — programação contínua"
          >
            <Tv className="w-3.5 h-3.5" /> TV
            {tvIsNew && <NewBadgePill testid="nav-tv-new-badge" />}
          </Link>
          {/* Rádio JurisTube — Iter166. Badge "Novo" removido a pedido
              do editor: o lançamento já foi absorvido pela audiência e
              a barra ficava sobrecarregada de sinalizações. Mantém o
              ícone de rádio para reforço visual, sem pill. */}
          <Link
            to="/radio"
            className="relative inline-flex items-center gap-1 hover:text-amber-300 transition-colors font-semibold"
            data-testid="nav-radio"
            title="Rádio JurisTube — podcasts em áudio com transcrição"
          >
            <Radio className="w-3.5 h-3.5" /> Rádio
          </Link>
          <Link to="/radar" className="relative inline-flex items-center gap-1 hover:text-amber-300 transition-colors font-semibold" data-testid="nav-radar" title="Radar — notícias jurídicas e Radar Constitucional">
            <RadarIcon className="w-3.5 h-3.5" /> Radar
            {radarIsNew && <NewBadgePill testid="nav-radar-new-badge" />}
          </Link>
          {/* App / PWA — Iter166. Incorporado ao índice do topo para dar
              caminho de descoberta ao guia de instalação e ao kit de
              divulgação, que antes só chegavam por link direto. Mesmo
              padrão visual da TV/Rádio (ícone Smartphone + label). */}
          <Link
            to="/app"
            className="relative inline-flex items-center gap-1 hover:text-amber-300 transition-colors font-semibold"
            data-testid="nav-app"
            title="Instale o JurisTube como aplicativo no celular ou desktop"
          >
            <Smartphone className="w-3.5 h-3.5" /> App
          </Link>
          <Link to="/autores" className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="nav-authors"><Users className="w-3.5 h-3.5" /> Autores</Link>
          <Link to="/colunistas" onClick={() => markVisited("artigos")} className="relative inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="nav-colunistas">
            <Newspaper className="w-3.5 h-3.5" /> Artigos{hasNewSince("artigos") && <NewDot />}
          </Link>
          <Link to="/comunidade" className="relative inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="nav-comunidade">
            <MessagesSquare className="w-3.5 h-3.5" /> Comunidade
            {comunidadeIsNew && <NewBadgePill testid="nav-comunidade-new-badge" />}
          </Link>
          <Link to="/temas" className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="nav-themes"><Tags className="w-3.5 h-3.5" /> Temas</Link>
          <Link to="/livraria" className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="nav-livraria"><BookOpen className="w-3.5 h-3.5" /> Livraria</Link>
        </nav>

        <div className="flex items-center gap-1.5 shrink-0">
          {/* Iter168 — Busca compactada: pill "🔍 Buscar" que vai
              direto para /busca (onde já existe o input principal
              com filtros). Antes ficava um <form> inline com input
              texto + botão "Ir" (~180px) que empurrava
              Entrar/Sobre para fora da tela em laptops 14".
              Agora ocupa ~92px e mantém a affordance visual da
              lupa vermelha. */}
          <Link
            to="/busca"
            data-testid="nav-search-link"
            className="hidden sm:inline-flex items-center gap-1.5 bg-slate-900/80 border border-slate-700 hover:border-red-500/60 rounded-full px-3 py-1.5 text-xs font-medium text-slate-200 hover:text-white transition"
            title="Buscar no acervo"
          >
            <Search className="w-3.5 h-3.5 text-red-400" />
            Buscar
          </Link>

          {/* Iter179 — Ajuste de tamanho do texto direto no header.
              Público sênior encontra o controle sem precisar rolar até
              o rodapé. Modo compacto: 3 pills (A / A+ / A++) com ~90px
              de largura total. Aria-label completo para leitores de tela.
              (Iter307.2) Restringido a ≥2xl (1536px+) porque em
              notebooks 13–15" (1366/1440) o combo A/A+/A++ empurrava
              Entrar/Sobre para fora do viewport a 100% de zoom. Nesses
              tamanhos, a acessibilidade tipográfica segue disponível
              pelo rodapé (`<LanguageBar />` + TextSizeControl) e pelo
              zoom nativo do browser. */}
          <div
            className="hidden 2xl:inline-flex items-center gap-1 mr-0.5"
            data-testid="nav-text-size"
            aria-label="Ajuste rápido de tamanho do texto"
          >
            <TextSizeControl compact testIdPrefix="nav-text-size" />
          </div>

          {user ? (
            <>
              <NotificationsBadge />
              <Link
                to="/minhas-colecoes"
                data-testid="nav-my-collections"
                className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 hover:text-white border border-slate-800 hover:border-red-500/60 rounded-full pl-1 pr-3 py-1 transition"
                title={user.name}
              >
                {user.avatar ? (
                  <img src={user.avatar} alt={`Avatar de ${user.name || user.email || "usuário"}`} className="w-6 h-6 rounded-full object-cover border border-slate-700" />
                ) : (
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-red-600/40 to-sky-600/40 border border-slate-700 flex items-center justify-center text-[10px] font-bold text-white">
                    {((user.name || user.email || "?").split(" ").slice(0, 2).map((w) => w[0] || "").join("") || "?").toUpperCase()}
                  </span>
                )}
                <span className="truncate max-w-[80px]">{user.name?.split(" ")[0] || "Conta"}</span>
              </Link>
            </>
          ) : (
            <Link
              to="/conta"
              data-testid="nav-login"
              className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 hover:text-white border border-slate-800 hover:border-red-500/60 rounded-full px-2.5 py-1.5 transition"
            >
              <LogIn className="w-3.5 h-3.5 text-red-400" />
              Entrar
            </Link>
          )}

          <Link
            to="/sobre"
            data-testid="nav-about"
            className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 hover:text-white border border-slate-800 hover:border-sky-500/60 rounded-full px-2.5 py-1.5 transition whitespace-nowrap"
          >
            <Info className="w-3.5 h-3.5 text-sky-400" />
            <span className="hidden 2xl:inline">Sobre o JurisTube</span>
            <span className="inline 2xl:hidden">Sobre</span>
          </Link>

          {/* Selo de acessibilidade de conteúdo — popover contextual
              que lista os recursos disponíveis na página atual + globais.
              Escondido abaixo de md para não competir com o botão de
              menu mobile (o popover já é acessível pelo footer). */}
          <div className="hidden md:inline-flex">
            <AccessibilityBadge variant="compact" />
          </div>

          <button className="md:hidden text-white" onClick={() => setOpen((v) => !v)} aria-label={open ? "Fechar menu" : "Abrir menu de navegação"} aria-expanded={open} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 bg-slate-950/95 backdrop-blur-xl">
          <div className="px-4 py-4 flex flex-col gap-3 text-sm text-slate-200">
            <Link to="/" data-testid="mobile-nav-home">Início</Link>
            <Link to="/podcasts-de-video" data-testid="mobile-nav-podcasts">Podcasts de Vídeo</Link>
            <Link to="/tv" data-testid="mobile-nav-tv" className="inline-flex items-center gap-2 text-amber-300 font-semibold">
              <Tv className="w-3.5 h-3.5" /> TV JurisTube
              {tvIsNew && <NewBadgePill mobile testid="mobile-nav-tv-new-badge" />}
            </Link>
            <Link to="/radio" data-testid="mobile-nav-radio" className="inline-flex items-center gap-2 text-amber-300 font-semibold">
              <Radio className="w-3.5 h-3.5" /> Rádio JurisTube
            </Link>
            <Link to="/radar" data-testid="mobile-nav-radar" className="inline-flex items-center gap-2 text-amber-300 font-semibold">
              <RadarIcon className="w-3.5 h-3.5" /> Radar
              {radarIsNew && <NewBadgePill mobile testid="mobile-nav-radar-new-badge" />}
            </Link>
            <Link to="/app" data-testid="mobile-nav-app" className="inline-flex items-center gap-2 text-amber-300 font-semibold">
              <Smartphone className="w-3.5 h-3.5" /> App
            </Link>
            <Link to="/autores" data-testid="mobile-nav-authors">Autores</Link>
            <Link to="/colunistas" data-testid="mobile-nav-colunistas">Artigos</Link>
            <Link to="/comunidade" data-testid="mobile-nav-comunidade" className="inline-flex items-center gap-2">
              Comunidade
              {comunidadeIsNew && <NewBadgePill mobile testid="mobile-nav-comunidade-new-badge" />}
            </Link>
            <Link to="/temas" data-testid="mobile-nav-themes">Temas</Link>
            <Link to="/livraria" data-testid="mobile-nav-livraria">Livraria</Link>
            <Link to="/shorts" data-testid="mobile-nav-shorts">Shorts</Link>
            <Link to="/fototeca" data-testid="mobile-nav-fototeca">Fototeca</Link>
            <Link to="/sobre-colecoes" data-testid="mobile-nav-about-collections">Coleções para turmas</Link>
            <Link to="/guia-professores" data-testid="mobile-nav-teacher-guide">Guia Professores(as)</Link>
            {user ? (
              <Link to="/minhas-colecoes" data-testid="mobile-nav-my-collections">Minhas coleções</Link>
            ) : (
              <Link to="/conta" data-testid="mobile-nav-login">Entrar / Criar conta</Link>
            )}
            <Link to="/sobre" data-testid="mobile-nav-about">Sobre o JurisTube</Link>
            <Link to={isAdmin ? "/admin" : "/admin/login"} data-testid="mobile-admin-link">{isAdmin ? "Painel Admin" : "Acessar Admin"}</Link>
            <form onSubmit={submit} className="flex gap-2 mt-2">
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar"
                className="flex-1 bg-slate-900 border border-slate-800 rounded-full px-3 py-2 text-sm"
                data-testid="mobile-search-input"
              />
              <button type="submit" className="btn-primary !py-2 !px-4" data-testid="mobile-search-submit">Ir</button>
            </form>
            {/* Iter179 — controle de tamanho do texto também no drawer mobile */}
            <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between flex-wrap gap-2">
              <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Tamanho do texto</span>
              <TextSizeControl compact testIdPrefix="mobile-nav-text-size" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
